import { Link } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useOrg } from "../lib/session";
import { ago } from "../lib/format";
import type { Source, Suggestion } from "../lib/types";
import { useEntitlements } from "../lib/plans";
import { Collapsible, countLabel, Empty, ErrorText, Field, Form, Loading, PageHeader, str } from "../components/ui";

type Mode = "file" | "site" | "text";

const fileLabels: Record<string, string> = { pdf: "PDF", docx: "Word", txt: "Text file", md: "Markdown", csv: "CSV" };

function describe(s: Source): string {
  switch (s.source_type) {
    case "file":
      return [fileLabels[s.file_type ?? ""] ?? "File", s.page_count > 0 && `${s.page_count} page${s.page_count === 1 ? "" : "s"}`].filter(Boolean).join(" · ");
    case "site":
      return s.status === "ready" ? `Website · ${s.page_count} page${s.page_count === 1 ? "" : "s"}` : `Website · up to ${s.max_pages ?? "?"} pages`;
    case "url":
      return "Web page";
    default:
      return "Text";
  }
}

export default function Knowledge() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const plan = useEntitlements();
  const canFiles = plan.enabled("knowledge_files");
  const canWeb = plan.enabled("knowledge_web_pages");
  const [mode, setMode] = useState<Mode>(canFiles ? "file" : canWeb ? "site" : "text");
  const [open, setOpen] = useState<string | null>(null);
  const sources = useQuery({
    queryKey: [orgId, "sources"],
    queryFn: () => api<{ sources: Source[] }>(`${base}/knowledge/sources`),
    // Poll only while something is still being processed.
    refetchInterval: (q) => (q.state.data?.sources.some((s) => s.status === "pending" || s.status === "processing") ? 3000 : false),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [orgId, "sources"] });
  const remove = useMutation({
    mutationFn: (id: string) => api(`${base}/knowledge/sources/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const [results, setResults] = useState<{ title: string; content: string; similarity: number }[] | null>(null);
  const maxChars = plan.limit("knowledge_source_characters");
  const maxPages = plan.limit("knowledge_site_pages") ?? 25;
  return (
    <>
      <PageHeader title="Knowledge" />
      <p className="muted page-intro">Your AI agents answer from these sources and their instructions. Saved answers go out as they are, without a new AI reply.</p>
      {plan.enabled("agent_learning") && <TeamSuggestions />}
      <SavedAnswers />
      <section className="card add-source">
        <div className="card-head">
          <h2>Add knowledge</h2>
          <div className="segmented" role="tablist" aria-label="Add from">
            {canFiles && <button role="tab" aria-selected={mode === "file"} className={mode === "file" ? "" : "secondary"} onClick={() => setMode("file")}>Documents</button>}
            {canWeb && <button role="tab" aria-selected={mode === "site"} className={mode === "site" ? "" : "secondary"} onClick={() => setMode("site")}>Website</button>}
            <button role="tab" aria-selected={mode === "text"} className={mode === "text" ? "" : "secondary"} onClick={() => setMode("text")}>Text</button>
          </div>
        </div>
        {mode === "file" && <UploadFiles onDone={refresh} />}
        {mode === "site" && (
          <Form submitLabel="Import" onSubmit={async (d) => {
            const whole = d.get("whole") === "on";
            await api(`${base}/knowledge/sources`, { method: "POST", json: { title: str(d, "title"), url: str(d, "url"), ...(whole && { crawl: true, max_pages: maxPages }) } });
            refresh();
          }}>
            <Field label="Website or page address"><input name="url" type="url" required placeholder="https://www.example.com" /></Field>
            <label className="check"><input type="checkbox" name="whole" defaultChecked /> Read the whole website (up to {maxPages} pages), not just this page</label>
            <Field label="Title (optional)"><input name="title" maxLength={200} placeholder="e.g. Our website" /></Field>
            <p className="muted small">We read the public pages linked from this address and its sitemap, and skip carts, logins and other websites. Sites built entirely with JavaScript may not be readable; upload or paste their content instead.</p>
          </Form>
        )}
        {mode === "text" && (
          <Form submitLabel="Add source" onSubmit={async (d) => {
            await api(`${base}/knowledge/sources`, { method: "POST", json: { title: str(d, "title"), text: str(d, "text") } });
            refresh();
          }}>
            <Field label="Title"><input name="title" required maxLength={200} placeholder="e.g. Delivery and returns" /></Field>
            <Field label={`Content (up to ${maxChars?.toLocaleString() ?? "—"} characters)`}><textarea name="text" rows={8} required maxLength={maxChars ?? undefined} /></Field>
          </Form>
        )}
      </section>
      <ErrorText error={remove.error} />
      {sources.isLoading ? <Loading /> : sources.data!.sources.length === 0 ? <Empty>No sources yet.</Empty> : (
        <table className="table">
          <thead><tr><th>Title</th><th>Type</th><th>Status</th><th>Passages</th><th>Added</th><th></th></tr></thead>
          <tbody>
            {sources.data!.sources.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.title}
                  {s.file_name && s.file_name !== s.title && <div className="muted small">{s.file_name}</div>}
                  {s.url && <div className="muted small">{s.url}</div>}
                  {s.truncated && <div className="small notice-text">Only the first {maxChars?.toLocaleString()} characters were used (your plan's limit per source).</div>}
                  {s.pages && s.pages.length > 0 && (
                    <>
                      <button className="link small" onClick={() => setOpen(open === s.id ? null : s.id)}>{open === s.id ? "Hide pages" : "Show pages read"}</button>
                      {open === s.id && (
                        <ul className="small page-list">
                          {s.pages.map((p) => <li key={p.url}><a href={p.url} target="_blank" rel="noreferrer noopener">{p.title || p.url}</a></li>)}
                        </ul>
                      )}
                    </>
                  )}
                </td>
                <td>{describe(s)}</td>
                <td>
                  {s.status === "processing" && s.source_type === "site" ? "reading pages…" : s.status}
                  {s.failure_reason && <div className="error small">{s.failure_reason}</div>}
                  {s.status === "failed" && (
                    <button className="link" onClick={async () => { await api(`${base}/knowledge/sources/${s.id}/retry`, { method: "POST" }); refresh(); }}>Retry</button>
                  )}
                </td>
                <td className="num">{s.chunk_count}</td>
                <td>{ago(s.created_at)}</td>
                <td><button className="link" onClick={() => confirm("Remove this source?") && remove.mutate(s.id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <section className="card">
        <h2>Test a question</h2>
        <Form submitLabel="Search" onSubmit={async (d) => {
          const r = await api<{ results: { title: string; content: string; similarity: number }[] }>(`${base}/knowledge/search`, { method: "POST", json: { query: str(d, "q") } });
          setResults(r.results);
        }}>
          <Field label="What would a customer ask?"><input name="q" required /></Field>
        </Form>
        {results && (results.length === 0 ? <Empty>Nothing relevant found. The agent would say it doesn't know.</Empty> : (
          <ol>
            {results.map((r, i) => (
              <li key={i}><b>{r.title}</b> <span className="muted small">({Math.round(r.similarity * 100)}% match)</span><p className="small">{r.content.slice(0, 400)}</p></li>
            ))}
          </ol>
        ))}
      </section>
    </>
  );
}

const MAX_UPLOAD_MB = 15;
const ACCEPT = ".pdf,.docx,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/csv";

interface UploadItem {
  name: string;
  state: "waiting" | "uploading" | "done" | "error";
  message?: string;
}

// UploadFiles sends documents one at a time, so each gets its own result.
function UploadFiles({ onDone }: { onDone: () => void }) {
  const { base } = useOrg();
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const busy = items.some((i) => i.state === "uploading" || i.state === "waiting");

  async function send(files: File[]) {
    if (busy || files.length === 0) return;
    const list: UploadItem[] = files.map((f) => ({ name: f.name, state: "waiting" }));
    setItems(list);
    const update = (i: number, patch: Partial<UploadItem>) => setItems((cur) => cur.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f.size > MAX_UPLOAD_MB << 20) {
        update(i, { state: "error", message: `Larger than ${MAX_UPLOAD_MB} MB.` });
        continue;
      }
      update(i, { state: "uploading" });
      const body = new FormData();
      body.append("file", f);
      try {
        const r = await api<{ truncated: boolean; page_count: number }>(`${base}/knowledge/files`, { method: "POST", body });
        update(i, { state: "done", message: r.truncated ? "Added — but it's longer than your plan allows per source, so only the first part was used." : r.page_count ? `Added (${r.page_count} pages).` : "Added." });
        onDone();
      } catch (err) {
        update(i, { state: "error", message: err instanceof ApiError ? (Object.values(err.fields ?? {})[0] ?? err.message) : "Upload failed." });
      }
    }
  }

  return (
    <div
      className={`uploader${dragging ? " over" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); void send(Array.from(e.dataTransfer.files)); }}
    >
      <div className="inline">
        <label className={`button file-button${busy ? " disabled" : ""}`}>
          <input type="file" multiple accept={ACCEPT} disabled={busy} onChange={(e) => { void send(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
          {busy ? "Uploading…" : "Choose files"}
        </label>
        <span className="muted small">PDF, Word, text, Markdown or CSV · up to {MAX_UPLOAD_MB} MB each · Arabic and English</span>
      </div>
      {items.length > 0 && (
        <ul className="upload-list small">
          {items.map((it, i) => (
            <li key={i} className={`upload-${it.state}`}>
              <span>{it.name}</span>
              <span>{it.state === "uploading" ? "Reading…" : it.state === "waiting" ? "Waiting…" : it.message}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">Scanned PDFs (photos of pages) have no text to read. Export the original as PDF, or paste the text.</p>
    </div>
  );
}

// Answers your team gave in conversations the agent handed over, reviewed
// one at a time. Accepting one adds it to knowledge: the agent uses it as a
// reference and answers in its own words, it doesn't paste it back.
function TeamSuggestions() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "learning"];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ suggestions: Suggestion[]; pending: number }>(`${base}/learning-suggestions`) });
  const [edit, setEdit] = useState<{ id: string; question: string; answer: string } | null>(null);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [done, setDone] = useState({ added: 0, dismissed: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [keep, setKeep] = useState(true);
  const all = list.data?.suggestions ?? [];
  if (!list.data || (all.length === 0 && done.added + done.dismissed === 0)) return null;
  // Skipped ones go to the back of the queue.
  const queue = [...all.filter((s) => !skipped.includes(s.id)), ...all.filter((s) => skipped.includes(s.id))];
  const current = queue[0];
  const reviewed = done.added + done.dismissed;
  const total = reviewed + list.data.pending;
  const v = current && edit?.id === current.id ? edit : current ? { id: current.id, question: current.question, answer: current.answer } : null;
  async function act(action: "accept" | "dismiss") {
    if (!current || !v) return;
    setError(null); setBusy(true);
    try {
      const changed = v.question !== current.question || v.answer !== current.answer;
      const save = keep && v.answer.length <= 2000 && v.question.length <= 300;
      await api(`${base}/learning-suggestions/${current.id}/${action}`, { method: "POST", ...(action === "accept" && { json: { ...(changed && { question: v.question, answer: v.answer }), save_as_answer: save } }) });
      if (action === "accept" && save) qc.invalidateQueries({ queryKey: [orgId, "saved-answers"] });
      setDone((d) => (action === "accept" ? { ...d, added: d.added + 1 } : { ...d, dismissed: d.dismissed + 1 }));
      setEdit(null);
      await qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: [orgId, "sources"] });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card learn-queue">
      <div className="inline"><h2 style={{ margin: 0 }}>Learn from your team</h2><span className="spacer" />
        {total > 0 && <span className="muted small">{Math.min(reviewed + 1, total)} of {total}</span>}</div>
      {total > 0 && <div className="progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={reviewed}><span style={{ width: `${(reviewed / total) * 100}%` }} /></div>}
      <ErrorText error={error} />
      {!current || !v ? (
        <p className="learn-done">All caught up. {done.added > 0 && `${done.added} answer${done.added === 1 ? "" : "s"} added to knowledge`}{done.added > 0 && done.dismissed > 0 && ", "}{done.dismissed > 0 && `${done.dismissed} dismissed`}.</p>
      ) : (
        <div className="suggestion">
          <p className="muted small">Your team answered this in a conversation the agent handed over. Add it and SVENEX can answer it next time without handing over.</p>
          {edit?.id === current.id ? (
            <>
              <Field label="Customer asked"><textarea rows={2} value={v.question} maxLength={1000} onChange={(e) => setEdit({ ...v, question: e.target.value })} /></Field>
              <Field label="What the agent should know"><textarea rows={4} value={v.answer} maxLength={4000} onChange={(e) => setEdit({ ...v, answer: e.target.value })} /></Field>
              <p className="muted small">As a saved answer this text is sent word for word to the same question. Similar questions get an AI reply that uses it.</p>
            </>
          ) : (
            <dl className="learn-pair">
              <dt>Customer asked</dt><dd dir="auto">{current.question}</dd>
              <dt>{current.answered_by ?? "Your team"} answered</dt><dd dir="auto">{current.answer}</dd>
            </dl>
          )}
          <label className="check"><input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} /> Also keep it as a saved answer: the same question gets this reply right away, without a new AI reply</label>
          <div className="actions">
            <button disabled={busy || !v.question.trim() || !v.answer.trim()} onClick={() => act("accept")}>{edit?.id === current.id ? "Save and add to knowledge" : "Add to knowledge"}</button>
            {edit?.id !== current.id && <button className="secondary" disabled={busy} onClick={() => setEdit({ id: current.id, question: current.question, answer: current.answer })}>Edit first</button>}
            <button className="secondary" disabled={busy} onClick={() => act("dismiss")}>Dismiss</button>
            {queue.length > 1 && <button className="secondary" disabled={busy} onClick={() => { setEdit(null); setSkipped((s) => [...s.filter((x) => x !== current.id), current.id]); }}>Skip for now</button>}
            <span className="muted small">{current.agent_name ? `${current.agent_name} · ` : ""}{ago(current.created_at)} · <Link to={`/o/${orgId}/inbox/${current.thread_id}`}>conversation</Link></span>
          </div>
        </div>
      )}
    </section>
  );
}

interface SavedAnswer { id: string; questions: string[]; answer: string; answer_ar: string | null; active: boolean; source: "manual" | "team" | "suggested"; use_count: number; last_used_at: string | null; created_at: string; review_reason: string | null; review_since: string | null }
interface AnswerSuggestion { id: string; questions: string[]; answer: string; answer_ar: string | null; ask_count: number; people_count: number; last_asked_at: string }
const sourceLabel = { manual: "Added by hand", team: "From your team", suggested: "Suggested by SVENEX" };

// Suggested saved answers: questions customers keep asking whose answer
// doesn't change. Nothing is sent until someone saves it, and they can edit
// it first.
function AnswerSuggestions({ onSaved }: { onSaved: () => void }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "saved-answer-suggestions"];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ suggestions: AnswerSuggestion[] }>(`${base}/saved-answers/suggestions`) });
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const list = q.data?.suggestions ?? [];
  if (list.length === 0) return null;
  const done = () => { setOpen(null); qc.invalidateQueries({ queryKey: key }); onSaved(); };
  return (
    <div className="suggested">
      <h3 className="suggested-title">Suggested from repeated questions <span className="count">{countLabel(list.length)}</span></h3>
      <p className="muted small">Customers keep asking these, and the answer is the same every time. Check the wording, then save it so SVENEX can answer without a new AI reply. Questions about times, availability, bookings or someone's own order are never suggested.</p>
      <ErrorText error={error} />
      <ul className="saved-list">
        {list.map((sg) => (
          <li key={sg.id} className="suggestion">
            {open === sg.id ? (
              <SavedAnswerForm initial={{ id: sg.id, questions: sg.questions, answer: sg.answer, answer_ar: sg.answer_ar, active: true, source: "suggested", use_count: 0, last_used_at: null, created_at: sg.last_asked_at, review_reason: null, review_since: null }}
                submitTo={`${base}/saved-answers/suggestions/${sg.id}/accept`} submitLabel="Save as saved answer" onDone={done} onCancel={() => setOpen(null)} />
            ) : (
              <>
                <div className="saved-q">{sg.questions.map((x, i) => <span key={i} dir="auto">{x}</span>)}</div>
                <p className="saved-a" dir="auto">{sg.answer}</p>
                {sg.answer_ar && <p className="saved-a" dir="rtl" lang="ar">{sg.answer_ar}</p>}
                <div className="saved-foot">
                  <span className="muted small">Asked {countLabel(sg.ask_count)} times by {countLabel(sg.people_count)} people · last {ago(sg.last_asked_at)}</span>
                  <span className="spacer" />
                  <button className="small" onClick={() => setOpen(sg.id)}>Review and save</button>
                  <button className="secondary small" onClick={async () => { setError(null); try { await api(`${base}/saved-answers/suggestions/${sg.id}/dismiss`, { method: "POST" }); qc.invalidateQueries({ queryKey: key }); } catch (e) { setError(e); } }}>Dismiss</button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Saved answers: the company's own replies to repeat questions, sent as they
// are (no new AI reply). Matching is strict; anything else goes to the AI.
function SavedAnswers() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "saved-answers"];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ answers: SavedAnswer[]; enabled: boolean; sent_30d: number; max: number }>(`${base}/saved-answers`) });
  const [editing, setEditing] = useState<SavedAnswer | "new" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [test, setTest] = useState("");
  const [result, setResult] = useState<{ matched: boolean; reply?: string; reason?: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const d = q.data;
  const answers = d?.answers ?? [];
  return (
    <section className="card saved-answers" id="saved-answers">
      <div className="card-head">
        <h2 className="card-title">Saved answers {answers.length > 0 && <span className="count">{countLabel(answers.length)}</span>}</h2>
        {d && (
          <label className="inline small switch-line">
            <span className="switch"><input type="checkbox" checked={d.enabled} onChange={async (e) => { setError(null); try { await api(`${base}/saved-answers/settings`, { method: "PUT", json: { enabled: e.target.checked } }); refresh(); } catch (err) { setError(err); } }} /><span /></span>
            {d.enabled ? "On" : "Off"}
          </label>
        )}
      </div>
      <p className="muted small">When a customer asks one of these questions, SVENEX sends your answer right away, with no new AI reply. It only does this when the question clearly matches; anything else still gets an AI reply.</p>
      {d && d.sent_30d > 0 && <p className="saved-stat"><span className="kchip saved">Saved answer</span> Sent {countLabel(d.sent_30d)} time{d.sent_30d === 1 ? "" : "s"} in the last 30 days, each one an AI reply SVENEX didn't need to write.</p>}
      <ErrorText error={error} />
      {q.isLoading ? <Loading /> : (
        <>
          <AnswerSuggestions onSaved={refresh} />
          {answers.length === 0 && editing !== "new" && <Empty>No saved answers yet. Add your most common questions, or keep your team's answers from the review above.</Empty>}
          <Collapsible items={answers} first={5} noun="answers" className="saved-list" render={(a) => (
            <li key={a.id} className={a.active ? "" : "off"}>
              {editing !== "new" && editing?.id === a.id ? <SavedAnswerForm initial={a} onDone={() => { setEditing(null); refresh(); }} /> : (
                <>
                  {a.review_reason && (
                    <div className="review-flag" role="status">
                      <span><b>Please check:</b> {a.review_reason}</span>
                      <button className="secondary small" onClick={async () => { setError(null); try { await api(`${base}/saved-answers/${a.id}`, { method: "PATCH", json: { reviewed: true } }); refresh(); } catch (e) { setError(e); } }}>Still correct</button>
                      <button className="small" onClick={() => setEditing(a)}>Edit answer</button>
                    </div>
                  )}
                  <div className="saved-q">{a.questions.map((x, i) => <span key={i} dir="auto">{x}</span>)}</div>
                  <p className="saved-a" dir="auto">{a.answer}</p>
                  {a.answer_ar && <p className="saved-a" dir="rtl" lang="ar">{a.answer_ar}</p>}
                  <div className="saved-foot">
                    <span className="muted small">{sourceLabel[a.source] ?? "Added by hand"} · sent {countLabel(a.use_count)} time{a.use_count === 1 ? "" : "s"}{a.last_used_at && `, last ${ago(a.last_used_at)}`}{!a.active && " · paused"}</span>
                    <span className="spacer" />
                    <button className="secondary small" onClick={() => setEditing(a)}>Edit</button>
                    <button className="secondary small" onClick={async () => { setError(null); try { await api(`${base}/saved-answers/${a.id}`, { method: "PATCH", json: { active: !a.active } }); refresh(); } catch (e) { setError(e); } }}>{a.active ? "Pause" : "Turn on"}</button>
                    <button className="secondary small" onClick={async () => { if (!confirm("Remove this saved answer?")) return; setError(null); try { await api(`${base}/saved-answers/${a.id}`, { method: "DELETE" }); refresh(); } catch (e) { setError(e); } }}>Remove</button>
                  </div>
                </>
              )}
            </li>
          )} />
          {editing === "new" ? <SavedAnswerForm onDone={() => { setEditing(null); refresh(); }} /> : (
            <div className="inline" style={{ marginTop: ".75rem" }}>
              <button className="secondary" disabled={!!d && answers.length >= d.max} onClick={() => setEditing("new")}>Add a saved answer</button>
            </div>
          )}
          {answers.length > 0 && (
            <form className="saved-test" onSubmit={async (e) => {
              e.preventDefault();
              if (!test.trim()) return;
              setError(null);
              try { setResult(await api(`${base}/saved-answers/test`, { method: "POST", json: { message: test } })); } catch (err) { setError(err); }
            }}>
              <input value={test} onChange={(e) => { setTest(e.target.value); setResult(null); }} maxLength={1000} placeholder="Try a customer message, e.g. do you have parking?" aria-label="Test a customer message" dir="auto" />
              <button type="submit" className="secondary">Test</button>
            </form>
          )}
          {result && (result.matched
            ? <p className="small"><span className="kchip saved">Saved answer</span> <span dir="auto">{result.reply}</span></p>
            : <p className="small"><span className="kchip ai">AI reply</span> {result.reason === "long" ? "Long messages always get an AI reply." : "No clear match, so the AI would answer this one."}</p>)}
        </>
      )}
    </section>
  );
}

function SavedAnswerForm({ initial, onDone, onCancel, submitTo, submitLabel }: { initial?: SavedAnswer; onDone: () => void; onCancel?: () => void; submitTo?: string; submitLabel?: string }) {
  const { base } = useOrg();
  const [questions, setQuestions] = useState<string[]>(initial?.questions.length ? initial.questions : [""]);
  const [answer, setAnswer] = useState(initial?.answer ?? "");
  const [answerAR, setAnswerAR] = useState(initial?.answer_ar ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <form className="form saved-form" onSubmit={async (e) => {
      e.preventDefault();
      setBusy(true); setError(null);
      const json = { questions: questions.filter((x) => x.trim()), answer, ...(answerAR.trim() ? { answer_ar: answerAR } : initial && !submitTo ? { clear_answer_ar: true } : {}) };
      try {
        if (submitTo) await api(submitTo, { method: "POST", json });
        else await api(initial ? `${base}/saved-answers/${initial.id}` : `${base}/saved-answers`, { method: initial ? "PATCH" : "POST", json });
        onDone();
      } catch (err) { setError(err); } finally { setBusy(false); }
    }}>
      <fieldset className="choices-col">
        <legend>How customers ask it (add the different ways they put it)</legend>
        {questions.map((x, i) => (
          <div key={i} className="inline">
            <input value={x} maxLength={300} dir="auto" placeholder={i === 0 ? "e.g. Do you have parking?" : "Another way to ask"} onChange={(e) => setQuestions(questions.map((y, j) => (j === i ? e.target.value : y)))} style={{ flex: 1 }} />
            {questions.length > 1 && <button type="button" className="secondary small" aria-label="Remove this question" onClick={() => setQuestions(questions.filter((_, j) => j !== i))}>Remove</button>}
          </div>
        ))}
        {questions.length < 10 && <button type="button" className="secondary small" style={{ justifySelf: "start" }} onClick={() => setQuestions([...questions, ""])}>Add another way</button>}
      </fieldset>
      <Field label="Answer"><textarea rows={3} maxLength={2000} value={answer} dir="auto" onChange={(e) => setAnswer(e.target.value)} required /></Field>
      <Field label="Answer in Arabic (optional, sent to customers who write in Arabic)"><textarea rows={3} maxLength={2000} value={answerAR} dir="rtl" lang="ar" onChange={(e) => setAnswerAR(e.target.value)} /></Field>
      <ErrorText error={error} />
      <div className="inline">
        <button type="submit" disabled={busy}>{busy ? "Saving…" : submitLabel ?? (initial ? "Save changes" : "Add saved answer")}</button>
        <button type="button" className="secondary" onClick={onCancel ?? onDone}>Cancel</button>
      </div>
    </form>
  );
}
